import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_QUOTATION } from '../../apollo/queries/quotationQueries';
import '../css/QuotationDetailPage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const QuotationDetailPage = () => {
  const [searchParams] = useSearchParams();
  const quotationId = searchParams.get('id');
  const navigate = useNavigate();

  // GraphQL 쿼리로 실제 견적 데이터 가져오기
  const { data, loading, error } = useQuery(GET_QUOTATION, {
    variables: { id: parseInt(quotationId) },
    skip: !quotationId,
    errorPolicy: 'all'
  });

  // 이미지 파싱 함수
  const parseProductImage = (images) => {
    if (!images) return null;
    
    try {
      if (typeof images === 'string') {
        const cleanedImages = images.trim();
        if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
          if (cleanedImages.startsWith('[')) {
            const parsedImages = JSON.parse(cleanedImages);
            if (Array.isArray(parsedImages) && parsedImages.length > 0) {
              return parsedImages[0];
            }
          } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
            return cleanedImages;
          }
        }
      } else if (Array.isArray(images) && images.length > 0) {
        return images[0];
      }
    } catch (error) {
      console.warn('이미지 파싱 오류:', error);
    }
    
    return null;
  };

  // 상태 표시 함수
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return '견적 대기';
      case 'COMPLETED': return '견적 완료';
      case 'REJECTED': return '견적 거절';
      default: return status;
    }
  };

  // 추가요청사항 파싱 함수
  const parseAdditionalRequest = (message) => {
    if (!message) return null;
    
    // 실제 데이터 형식에 맞는 패턴: "=== 추가 요청사항 ===\n내용..."
    const additionalRequestMatch = message.match(/=== 추가 요청사항 ===\s*\n([\s\S]*?)$/);
    if (additionalRequestMatch && additionalRequestMatch[1]) {
      const content = additionalRequestMatch[1].trim();
      return content || null;
    }
    return null;
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="quotation-detail-container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>견적 상세 정보를 불러오는 중입니다...</div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="quotation-detail-container">
        <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
          <div>견적 정보를 불러오는 중 오류가 발생했습니다: {error.message}</div>
          <button 
            onClick={() => navigate(-1)}
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 견적 데이터가 없는 경우
  if (!data?.quotation) {
    return (
      <div className="quotation-detail-container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>견적 정보를 찾을 수 없습니다.</div>
          <button 
            onClick={() => navigate(-1)}
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const quotation = data.quotation;

  return (
      <div className="quotation-detail-container">
      <div className="breadcrumb">
        <span>홈 &gt; 회원 정보 관리 &gt; 견적내역</span>
      </div>
        <div className="page-header">
          <div className="page-title-bar"></div>
          <h2>견적 상세 내역</h2>
        </div>
        <div className="quotation-info-box">
          <div className="info-grid">
            <div className="info-row"><div className="info-label">견적 번호</div><div className="info-value">{quotation.id}</div></div>
            <div className="info-row"><div className="info-label">견적 일자</div><div className="info-value">{new Date(quotation.createdAt).toLocaleDateString('ko-KR')}</div></div>
            <div className="info-row"><div className="info-label">고객명</div><div className="info-value">{quotation.user.name} ({quotation.user.company || '회사명 없음'})</div></div>
            <div className="info-row"><div className="info-label">고객 이메일</div><div className="info-value">{quotation.user.email}</div></div>
            <div className="info-row"><div className="info-label">연락처</div><div className="info-value">{quotation.user.phone || quotation.user.mobile || '연락처 없음'}</div></div>
            <div className="info-row"><div className="info-label">배송지 주소</div><div className="info-value">
              {quotation.user.zipCode || quotation.user.address ? 
                `${quotation.user.zipCode || ''} ${quotation.user.address || ''} ${quotation.user.addressDetail || ''}`.trim() 
                : '주소 정보 없음'}
            </div></div>
            <div className="info-row"><div className="info-label">견적 상태</div><div className="info-value">{getStatusText(quotation.status)}</div></div>
            <div className="info-row"><div className="info-label">이메일 발송</div><div className="info-value">{quotation.status === 'COMPLETED' ? '발송 완료' : '대기중'}</div></div>
          </div>
          <button className="btn-pdf">PDF 출력하기</button>
        </div>

        <div className="customer-detail-section">
          <div className="customer-detail-header">
            <h3>고객 상세 정보</h3>
          </div>
          <div className="customer-detail-grid">
            <div className="customer-detail-row">
              <div className="customer-detail-label">고객명</div>
              <div className="customer-detail-value">{quotation.user.name}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">회사명</div>
              <div className="customer-detail-value">{quotation.user.company || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">대표자명</div>
              <div className="customer-detail-value">{quotation.user.ceoName || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">부서/직책</div>
              <div className="customer-detail-value">{quotation.user.department || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">사업자등록번호</div>
              <div className="customer-detail-value">{quotation.user.businessNumber || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">전화번호</div>
              <div className="customer-detail-value">{quotation.user.phone || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">휴대폰</div>
              <div className="customer-detail-value">{quotation.user.mobile || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">팩스</div>
              <div className="customer-detail-value">{quotation.user.fax || '-'}</div>
            </div>
            <div className="customer-detail-row">
              <div className="customer-detail-label">이메일</div>
              <div className="customer-detail-value">{quotation.user.email}</div>
            </div>
                         <div className="customer-detail-row customer-address-row">
               <div className="customer-detail-label">기본 주소</div>
               <div className="customer-detail-value">
                 {quotation.user.zipCode && (
                   <div className="address-line">
                     <span className="zip-code">[{quotation.user.zipCode}]</span>
                   </div>
                 )}
                 {quotation.user.address && (
                   <div className="address-line">{quotation.user.address}</div>
                 )}
                 {quotation.user.addressDetail && (
                   <div className="address-line">{quotation.user.addressDetail}</div>
                 )}
                 {!quotation.user.zipCode && !quotation.user.address && (
                   <div className="address-line">기본 주소가 등록되지 않았습니다.</div>
                 )}
               </div>
             </div>
             <div className="customer-detail-row customer-delivery-row">
               <div className="customer-detail-label">등록된 배송지</div>
               <div className="customer-detail-value">
                 {quotation.user.deliveryAddresses && quotation.user.deliveryAddresses.length > 0 ? (
                   <div className="delivery-addresses-list">
                     {quotation.user.deliveryAddresses.map((address, index) => (
                       <div key={address.id} className="delivery-address-item">
                         <div className="delivery-address-header">
                           <span className="delivery-address-name">
                             {address.name}
                             {address.isDefault && <span className="default-badge">기본</span>}
                           </span>
                           <span className="delivery-address-recipient">({address.recipient})</span>
                         </div>
                         <div className="delivery-address-details">
                           <div className="address-line">
                             <span className="zip-code">[{address.zipCode}]</span>
                           </div>
                           <div className="address-line">{address.address}</div>
                           {address.addressDetail && (
                             <div className="address-line">{address.addressDetail}</div>
                           )}
                           <div className="address-line">
                             <span className="contact-info">연락처: {address.phone}</span>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="address-line">등록된 배송지가 없습니다.</div>
                 )}
               </div>
             </div>
          </div>
        </div>

        <div className="product-list-section">
          <div className="product-list-header">
            <label className="checkbox-container"><input type="checkbox" defaultChecked /></label>
            <span className="quote-number-header">견적번호 {quotation.id} (견적일: {new Date(quotation.createdAt).toLocaleDateString('ko-KR')})</span>
            <div className="header-labels">
              <span>제품명</span>
              <span>수량</span>
              <span>희망납기일</span>
              <span>상태</span>
            </div>
          </div>
          <div className="product-list">
            {quotation.quotationItems && quotation.quotationItems.length > 0 ? (
              quotation.quotationItems.map(item => {
                const productImage = parseProductImage(item.product.images);
                return (
                  <div className="product-item-row" key={item.id}>
                    <div className="col-1">
                      <label className="checkbox-container"><input type="checkbox" defaultChecked /></label>
                    </div>
                    <div className="col-2">
                      <ImageWithPlaceholder 
                        src={productImage} 
                        alt={item.product.name} 
                        className="admin-quotation-image"
                      />
                    </div>
                    <div className="col-3">
                      <div className="product-details">
                        <p className="product-name">{item.product.name}</p>
                        <p className="product-option">{item.product.specifications || '옵션 없음'}</p>
                      </div>
                    </div>
                    <div className="col-4">
                      <input type="text" defaultValue={item.quantity} className="quantity-input" />
                    </div>
                    <div className="col-5">
                      <input type="date" defaultValue={new Date().toISOString().split('T')[0]} className="date-input" />
                    </div>
                    <div className="col-6">
                      <div className="product-status">
                        <select defaultValue={quotation.status}>
                          <option value="PENDING">견적 대기</option>
                          <option value="COMPLETED">견적 완료</option>
                          <option value="REJECTED">견적 거절</option>
                        </select>
                        <select defaultValue={quotation.status === 'COMPLETED' ? 'sent' : 'pending'}>
                          <option value="sent">이메일 전송 완료</option>
                          <option value="pending">이메일 미전송</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                견적 항목이 없습니다.
              </div>
            )}
          </div>
        </div>
        
        <div className="inquiry-section">
          <div className="inquiry-label">문의 사항</div>
          <div className="inquiry-content">
            <p>{quotation.message || '문의 내용이 없습니다.'}</p>
          </div>
        </div>

        {/* 주문 제작일 경우에만 추가요청사항 표시 */}
        {(!quotation.quotationItems || quotation.quotationItems.length === 0) && parseAdditionalRequest(quotation.message) && (
          <div className="additional-request-section">
            <div className="inquiry-label">추가 요청사항</div>
            <div className="inquiry-content">
              <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                {parseAdditionalRequest(quotation.message)}
              </p>
            </div>
          </div>
        )}

        {/* 고객 첨부파일 섹션 */}
        {quotation.customerFiles && quotation.customerFiles.length > 0 && (
          <div className="customer-files-section">
            <div className="inquiry-label">고객 첨부파일</div>
            <div className="customer-files-list">
              {quotation.customerFiles.map((file, index) => (
                <div key={index} className="customer-file-item">
                  <span className="file-icon">📎</span>
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">({(file.size / 1024).toFixed(1)}KB)</span>
                  <div className="file-actions">
                    <button 
                      className="btn-download"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = file.url;
                        link.download = file.name;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      다운로드
                    </button>
                    {(file.name.toLowerCase().includes('.jpg') || 
                      file.name.toLowerCase().includes('.jpeg') || 
                      file.name.toLowerCase().includes('.png') || 
                      file.name.toLowerCase().includes('.gif') || 
                      file.name.toLowerCase().includes('.pdf')) && (
                      <button 
                        className="btn-preview"
                        onClick={() => window.open(file.url, '_blank')}
                      >
                        미리보기
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="memo-section">
          <div className="memo-label">관리자 메모</div>
          <div className="memo-content">
            <textarea defaultValue={quotation.adminReply || ''}></textarea>
          </div>
        </div>

      <div className="page-actions">
          <button className="btn-secondary" onClick={() => navigate(-1)}>
            <span className="arrow-icon">&lt;</span> 돌아가기
          </button>
          <button className="btn-primary">
            수정 <span className="arrow-icon">&gt;</span>
          </button>
      </div>
    </div>
  );
};

export default QuotationDetailPage; 